import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ReactElement, useMemo, useState } from 'react';

import { Icon } from 'components';
import ActionMenu from 'components/ActionMenu';
import { addCohostModalInstance } from 'components/AddCohostModal';
import { ButtonV3 } from 'components/ComponentV2';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import Avatar from 'components/Avatar';

import { useChatRoom } from 'hooks/useChatRoom';
import { useCohost } from 'hooks/useCohost';
import { useNotification } from 'hooks/useNotification';

import { CohostsResponse } from 'lib/models/cohost';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { useSocietiesSearch } from 'lib/contexts/societies-search-context';

import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './Hosts.module.scss';

export enum ConfirmationScreen {
  NO_SCREEN = 0,
  REMOVE = 1,
  STOP_COHOST = 2,
}

export const Hosts = (): ReactElement => {
  const router = useRouter();
  const { id } = router.query;

  const [t] = useTranslation('event-dashboard');
  const HostTabsOptions = [
    {
      id: '0',
      value: t('host-page.tabs.hosts.value'),
      label: t('host-page.tabs.hosts.label'),
    },
    {
      id: '1',
      value: t('host-page.tabs.invited.value'),
      label: t('host-page.tabs.invited.label'),
    },
  ];

  const { touchpoint, mutateTouchpoint } = useAttendeesFilter();
  const [selectedEventState, setSelectedEventState] = useState(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isRemoveButtonLoading, setIsRemoveButtonLoading] = useState(false);
  const [confirmationScreenSelected, setConfirmationScreenSelected] =
    useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

  const [removeHostDetails, setRemoveHostDetails] = useState({
    id: '',
    name: '',
  });
  const { addCohost, removeCohost, resendInviteMail } = useCohost();
  const notificationInstance = useNotification();
  const { setSearch } = useSocietiesSearch();

  const { title, creatable_for, cohost_applications, cohost_application } =
    touchpoint || {};
  const isCohost = cohost_application ? true : false;
  const cohostCurrentId = parseResponse<{ id: string }>(cohost_application)?.id;

  const cohostApplications = parseArrayResponse(
    cohost_applications as CohostsResponse
  );

  const { onContactClick } = useChatRoom();

  const isPending = !!cohostApplications.filter(
    (item) => item.status === 'pending'
  ).length;

  const getActionMenu = (
    id: string,
    isPending: boolean,
    name: string,
    stopCoHosting: boolean
  ) => {
    const menu = [
      {
        id: '1',
        label: stopCoHosting
          ? t('host-page.stop-co-hosting.label')
          : t('common.remove'),
        danger: true,
        onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setRemoveHostDetails({ id, name });
          setIsConfirmationModalOpen(true);
          setConfirmationScreenSelected(
            stopCoHosting
              ? ConfirmationScreen.STOP_COHOST
              : ConfirmationScreen.REMOVE
          );
        },
      },
    ];
    if (isPending) {
      menu.unshift({
        id: '2',
        label: t('host-page.reinvite.label'),
        danger: false,
        onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          event.stopPropagation();
          const response = await resendInviteMail(id);
          if (response) {
            notificationInstance.success({
              title: t('host-page.reinvite.sent.title'),
              message: t('host-page.reinvite.sent.message'),
            });
            mutateTouchpoint();
          }
        },
      });
    }

    return menu;
  };

  const {
    id: hostId,
    icon_url,
    name,
  } = creatable_for
    ? parseResponse<{
        icon_url: string;
        name: string;
        id: string;
      }>(creatable_for)
    : { icon_url: '', name: '', id: '' };

  const onAddCohostClick = async () => {
    if (hostId) {
      try {
        const cohostIds = await addCohostModalInstance({
          idsForCohost: {
            creatableForId: hostId,
            touchpointId: id as string,
          },
        });
        if (cohostIds.length > 0) {
          for (let i = 0; i < cohostIds.length; i++) {
            const response = await addCohost(id as string, cohostIds[i]);
            if (!response) {
              break;
            } else {
              mutateTouchpoint();
              notificationInstance.success({
                title:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-title')
                    : t('add-co-host-message.invitations-title'),
                message:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-message')
                    : t('add-co-host-message.invitations-message'),
              });
            }
          }
        }
        setSearch('');
      } catch (error) {
        noop;
      }
    }
  };

  const removeHostConfirmationScreenDesc = touchpoint ? (
    <div className={styles.removeCohostModalText}>
      <p>
        {t('host-page.remove-host.body1')} <span>{removeHostDetails.name}</span>{' '}
        {t('host-page.remove-host.body2')} <span>{title}</span>?
      </p>
    </div>
  ) : null;
  const stopHostConfirmationScreenDesc = touchpoint ? (
    <div className={styles.removeCohostModalText}>
      <p>
        {t('host-page.stop-co-hosting.message')}
        <span>{removeHostDetails.name}</span>?
      </p>
    </div>
  ) : null;

  const ConfirmationScreenProps = [
    {
      id: ConfirmationScreen.REMOVE,
      title: t('host-page.remove-host.title'),
      desc: removeHostConfirmationScreenDesc,
      buttonContent: t('host-page.remove-host.button'),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        if (removeHostDetails.id) {
          setIsRemoveButtonLoading(true);
          await removeCohost(removeHostDetails.id);
          mutateTouchpoint();
          setIsRemoveButtonLoading(false);
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        }
      },
      isPrimary: false,
    },
    {
      id: ConfirmationScreen.STOP_COHOST,
      title: t('host-page.stop-co-hosting.title'),
      desc: stopHostConfirmationScreenDesc,
      buttonContent: t('host-page.stop-co-hosting.button'),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        if (removeHostDetails.id) {
          setIsRemoveButtonLoading(true);
          await removeCohost(removeHostDetails.id);
          setIsRemoveButtonLoading(false);
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          router.push(PAGE_ROUTES.CAMPAIGNS);
        }
      },
      isPrimary: false,
    },
  ];

  const getConfirmationModal = () => {
    const screen = ConfirmationScreenProps.find(
      (item) => item.id === confirmationScreenSelected
    );
    if (screen) {
      const { title, desc, buttonContent, onCancel, onConfirm, isPrimary } =
        screen;
      return (
        <Modal
          isOpen={isConfirmationModalOpen}
          onRequestClose={() => setIsConfirmationModalOpen(false)}
          isFullScreen={false}
        >
          <Confirmation
            title={title}
            desc={desc || ''}
            buttonContent={buttonContent}
            onCancel={onCancel}
            isLoading={isRemoveButtonLoading}
            onConfirm={onConfirm}
            isPrimary={isPrimary}
            secondaryActionOnRight={true}
          />
        </Modal>
      );
    }
  };

  const hostData = useMemo(() => {
    const cohost = cohostApplications
      .filter((item) =>
        selectedEventState === 1
          ? item.status === 'pending'
          : item.status != 'pending'
      )
      .map((item) => {
        const { id: cohostId, cohost: cohostResponse, status } = item;
        const cohost =
          parseResponse<{ id: string; icon_url: string; name: string }>(
            cohostResponse
          );
        const { id, icon_url, name } = cohost;
        return (
          <tr key={id}>
            <td>
              <div className={styles.hostName}>
                <Avatar
                  name={name}
                  icon={icon_url}
                  size="xxsmall"
                  className={styles['host-image']}
                />
                {name}
              </div>
            </td>
            <td>
              {selectedEventState === 0
                ? status === 'rejected'
                  ? t('host-page.rejected')
                  : t('host-page.co-host')
                : t('host-page.invited')}
            </td>
            <td>
              {cohostCurrentId != cohostId && (
                <button
                  className={styles.sendMessageButton}
                  onClick={() => {
                    onContactClick(id, 'StudentSociety');
                  }}
                >
                  <Icon iconName="icon_send-message" size="xsmallplus" />
                </button>
              )}
              {(!isCohost || (isCohost && cohostCurrentId === cohostId)) && (
                <ActionMenu
                  options={getActionMenu(
                    cohostId,
                    item.status === 'pending',
                    name,
                    cohostCurrentId === cohostId
                  )}
                  triggerButtonClassName={styles.actionMenu}
                />
              )}
            </td>
          </tr>
        );
      });

    return (
      <table>
        <thead>
          <tr>
            <th>{t('host-page.table.head.host')}</th>
            <th>{t('host-page.table.head.status')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {selectedEventState === 0 && (
            <tr>
              <td>
                <div className={styles.hostName}>
                  <Avatar
                    name={name}
                    icon={icon_url}
                    size="xxsmall"
                    className={styles['host-image']}
                  />
                  {name}
                </div>
              </td>
              <td>{t('host-page.table.head.host')}</td>
              <td>
                {isCohost && (
                  <button
                    className={styles.sendMessageButton}
                    onClick={() => {
                      onContactClick(
                        hostId,
                        creatable_for.data.type === 'student_society'
                          ? 'StudentSociety'
                          : 'Company'
                      );
                    }}
                  >
                    <Icon iconName="icon_send-message" size="xsmallplus" />
                  </button>
                )}
              </td>
            </tr>
          )}
          {cohost}
        </tbody>
      </table>
    );
  }, [selectedEventState, cohostApplications, creatable_for]);

  return (
    <section className={styles.hosts}>
      <div className={styles.hostsHeader}>
        <p>{t('host-page.title')}</p>
        {!isCohost && (
          <ButtonV3
            color="primary"
            variant="contained"
            size="medium"
            startIcon="icon_mail"
            onClick={() => onAddCohostClick()}
          >
            {t('host-page.invite-co-host')}
          </ButtonV3>
        )}
      </div>
      <Tab.Group
        selectedIndex={selectedEventState}
        onChange={(index) => {
          setSelectedEventState(index);
        }}
      >
        {isPending && !isCohost && (
          <Tab.List className={styles.tabList}>
            {HostTabsOptions.map((tab) => {
              return (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    cn(styles.tab, { [styles.tabSelected]: selected })
                  }
                >
                  {tab.label}
                </Tab>
              );
            })}
          </Tab.List>
        )}
        <Tab.Panels className={styles.tabPanels}>
          <Tab.Panel>{hostData}</Tab.Panel>
          <Tab.Panel>{hostData}</Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
      {isConfirmationModalOpen ? getConfirmationModal() : null}
    </section>
  );
};
